import { Maths } from 'helpers/maths';
import {
  FormValues,
  Product
} from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import { recalculateProductsOnPropertiesChange } from 'screens/Applications&WorkOrders/StepsLayout/Components/ProductsStep/helpers';
import { calculateCarrierTotals } from 'screens/Applications&WorkOrders/StepsLayout/Components/ProductsStep/components/ProductsTable/helpers';

/**
 * Computes the new tank info values (totalProduct and tankCount)
 * while keeping overallRatePerAcre constant.
 */
export const computeTankInfo = (
  overallRatePerAcre: number,
  totalAcreage: number,
  tankSize: number | undefined
) => {
  const totalProduct = Maths.multiplyRound(overallRatePerAcre, totalAcreage);
  const tankCount = tankSize
    ? Maths.divideSafeRound(totalProduct, tankSize)
    : 0;
  return { totalProduct, tankCount };
};

/**
 * Updates carrier product totals within a product list.
 * Non-carrier products are returned as-is.
 */
export const applyCarrierTotals = (
  products: Product[],
  totalAcreage: number,
  tankInfo: {
    overallRatePerAcre: number;
    tankCount: number;
    tankSize: number;
    overallTotalProduct: number;
  },
  convertFn?: Function
): Product[] => {
  // Update carrier's appliedAreaValue before calculating totals
  const withUpdatedAcreage = products.map(p =>
    p.isCarrier
      ? {
          ...p,
          appliedAreaValue: totalAcreage,
          totalAppliedAcreage: totalAcreage
        }
      : p
  );

  const carrierTotals = calculateCarrierTotals(
    withUpdatedAcreage,
    tankInfo,
    convertFn
  );

  return withUpdatedAcreage.map(p =>
    p.isCarrier
      ? {
          ...p,
          totalProduct: carrierTotals.totalProduct,
          ratePerAcre: carrierTotals.ratePerAcre,
          ratePerTank: carrierTotals.ratePerTank,
          totalCost: carrierTotals.totalCost
        }
      : p
  );
};

/**
 * Returns the Formik field updates that follow from a new total applied
 * acreage: the total itself, the tank totals derived from a constant
 * overallRatePerAcre when tank info is enabled, and the recalculated
 * products when any exist. Fields are listed in the order they should be
 * written to the form.
 * @param values - Current form values
 * @param total - New total applied acreage
 * @param convertFn - Unit conversion function forwarded to the product math
 * @returns Field updates keyed by Formik field name
 */
export const getTotalAcreageUpdates = (
  values: FormValues,
  total: number,
  convertFn?: Function
): Partial<FormValues> => {
  const products = values.products ?? [];

  if (!values.isTankInfoEnabled || !values.overallRatePerAcre) {
    if (!products.length) return { totalAppliedAcreage: total };

    return {
      totalAppliedAcreage: total,
      products: recalculateProductsOnPropertiesChange(
        products,
        total,
        undefined,
        convertFn
      )
    };
  }

  const { totalProduct, tankCount } = computeTankInfo(
    values.overallRatePerAcre,
    total,
    values.tankSize
  );
  const tankUpdates = {
    totalAppliedAcreage: total,
    totalProduct,
    tankCount: tankCount || undefined
  };
  if (!products.length) return tankUpdates;

  const updatedProducts = recalculateProductsOnPropertiesChange(
    products,
    total,
    { tankCount },
    convertFn
  );
  const hasCarrier = updatedProducts.some(p => p.isCarrier);

  return {
    ...tankUpdates,
    products: hasCarrier
      ? applyCarrierTotals(
          updatedProducts,
          total,
          {
            overallRatePerAcre: values.overallRatePerAcre,
            tankCount,
            tankSize: values.tankSize || 0,
            overallTotalProduct: totalProduct
          },
          convertFn
        )
      : updatedProducts
  };
};
